import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Plus, Pencil, Trash2, Save, X, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  listProfessionals, listProfessionalCategories,
  createProfessional, updateProfessional, deleteProfessional,
} from './crmApi';

const EMPTY_FORM = { category: '', name: '', company: '', phone: '', email: '', notes: '' };

export default function CrmSettingsProfessionals() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  // Read `?category=` from the URL so the valuation-detail "Order appraisal"
  // deep-link lands us pre-filtered to the appraiser tab.
  const initialFilter = (() => {
    if (typeof window === 'undefined') return 'all';
    const p = new URLSearchParams(window.location.search).get('category');
    return p || 'all';
  })();
  const [filter, setFilter] = useState(initialFilter);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [pros, cats] = await Promise.all([
        listProfessionals(),
        listProfessionalCategories(),
      ]);
      setRows(pros);
      setCategories(cats);
    } catch (err) {
      toast({ title: 'Failed to load professionals',
              description: err?.response?.data?.detail || 'Try again.',
              variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { refresh(); }, [refresh]);

// Investor-oriented categories per the AI's investor playbook — grouped
// under a single "For investors" filter tab so realtors triage the pros
// most likely to matter on a rental / flip / 1031 deal.
const INVESTOR_CATEGORIES = new Set([
  'mortgage_lender', 'general_contractor',
  'accountant', 'property_manager', 'real_estate_attorney',
]);

  const filteredRows = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'investor_bundle') {
      return rows.filter((r) => INVESTOR_CATEGORIES.has(r.category));
    }
    return rows.filter((r) => r.category === filter);
  }, [rows, filter]);

  const groupedByCategory = useMemo(() => {
    const groups = {};
    filteredRows.forEach((r) => {
      (groups[r.category] ||= []).push(r);
    });
    return groups;
  }, [filteredRows]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY_FORM }); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      category: p.category, name: p.name || '', company: p.company || '',
      phone: p.phone || '', email: p.email || '', notes: p.notes || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.category) {
      toast({ title: 'Pick a category', variant: 'destructive' }); return;
    }
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' }); return;
    }
    setBusy(true);
    try {
      if (editing) await updateProfessional(editing.id, form);
      else await createProfessional(form);
      toast({ title: editing ? 'Updated' : 'Added to referral list' });
      setShowForm(false);
      await refresh();
    } catch (err) {
      toast({ title: 'Save failed',
              description: err?.response?.data?.detail || 'Check the fields.',
              variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} from your referral list?`)) return;
    try {
      await deleteProfessional(p.id);
      toast({ title: 'Removed' });
      await refresh();
    } catch (err) {
      toast({ title: 'Delete failed',
              description: err?.response?.data?.detail || 'Try again.',
              variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="crm-professionals">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-brand-green inline-flex items-center gap-2">
            <Users className="w-6 h-6" /> Trusted professionals
          </h1>
          <p className="text-brand-green/70 text-sm mt-1 max-w-2xl inline-flex items-start gap-2">
            <Sparkles className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0" />
            <span>Add the lenders, inspectors, attorneys, and other pros you trust. The AI reads
              from this list — when a caller asks "do you have someone who…", the AI recommends
              one of yours by name and phone. It never fabricates a referral.</span>
          </p>
        </div>
        <Button onClick={openNew} data-testid="pro-new-btn"
                className="bg-brand-green text-brand-cream hover:bg-brand-green/90 inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add professional
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="pro-category-filter">
        <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} testId="pro-filter-all">
          All ({rows.length})
        </FilterPill>
        <FilterPill active={filter === 'investor_bundle'}
                    onClick={() => setFilter('investor_bundle')}
                    testId="pro-filter-investor-bundle">
          For investors ({rows.filter((r) => INVESTOR_CATEGORIES.has(r.category)).length})
        </FilterPill>
        {categories.map((c) => {
          const count = rows.filter((r) => r.category === c.value).length;
          if (count === 0 && filter !== c.value) return null;
          return (
            <FilterPill key={c.value} active={filter === c.value}
                        onClick={() => setFilter(c.value)}
                        testId={`pro-filter-${c.value}`}>
              {c.label} ({count})
            </FilterPill>
          );
        })}
      </div>

      {loading ? (
        <p className="text-brand-green/60">Loading…</p>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-brand-green/70">Your referral list is empty. Add your first pro so the AI has someone to recommend.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByCategory).map(([cat, list]) => {
            const catInfo = categories.find((c) => c.value === cat);
            return (
              <div key={cat}>
                <div className="text-xs uppercase tracking-wide text-brand-green/60 mb-2">
                  {catInfo?.label || cat} · {list.length}
                </div>
                <ul className="space-y-2">
                  {list.map((p) => (
                    <li key={p.id} data-testid={`pro-row-${p.id}`}
                        className="bg-white rounded-xl p-4 shadow-sm flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-brand-green truncate">{p.name}</div>
                        {p.company && <div className="text-sm text-brand-green/70">{p.company}</div>}
                        <div className="text-xs text-brand-green/60 mt-1 flex flex-wrap gap-x-3">
                          {p.phone && <span>{p.phone}</span>}
                          {p.email && <span>{p.email}</span>}
                        </div>
                        {p.notes && <p className="text-xs text-brand-green/60 mt-1 line-clamp-2">{p.notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)}
                                data-testid={`pro-edit-${p.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(p)}
                                data-testid={`pro-delete-${p.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="pro-form-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit professional' : 'Add professional'}</DialogTitle>
            <DialogDescription>
              The AI will recommend this contact by name and phone when a caller needs their category.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="pro-cat">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger id="pro-cat" data-testid="pro-input-category">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="pro-name">Name *</Label>
              <Input id="pro-name" value={form.name} data-testid="pro-input-name"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pro-company">Company</Label>
                <Input id="pro-company" value={form.company} data-testid="pro-input-company"
                       onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="pro-phone">Phone</Label>
                <Input id="pro-phone" value={form.phone} data-testid="pro-input-phone"
                       placeholder="+13105550101"
                       onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="pro-email">Email</Label>
              <Input id="pro-email" type="email" value={form.email} data-testid="pro-input-email"
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="pro-notes">Notes (context for the AI)</Label>
              <Textarea id="pro-notes" value={form.notes} data-testid="pro-input-notes"
                        placeholder="Great with first-time buyers, replies within an hour…"
                        rows={2}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={busy} data-testid="pro-save-btn"
                    className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
              <Save className="w-4 h-4 mr-1" /> {busy ? 'Saving…' : (editing ? 'Update' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterPill({ active, onClick, testId, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              active
                ? 'bg-brand-green text-brand-cream'
                : 'bg-brand-cream text-brand-green hover:bg-brand-cream/80'
            }`}>
      {children}
    </button>
  );
}
